import React, { useContext, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  SET_PROVIDER_INFO,
  TOGGLE_FILTERING_ATTRIBUTE,
  VisitsGlobalContext,
} from '@mummssoftware/global-context';
import { formatHHMMtoMM, formatMMtoDec } from '@mummssoftware/utils/formatters';
import { ExcelStyle, GridApi, GridOptions } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import styled from 'styled-components';
import { ALL } from '@mummssoftware/utils/constants';
import { AppContext } from '../../context';
import defaults from '../../translations/en.json';
import { VisitSummaryContext } from '../Context/VisitSummaryContext';
import { SET_SUMMARY_GRID_API } from '../Context/constants';
import { Container, FullColumnDiv } from '../../components/layout/WrapperDiv';
import {
  commonColumnDefs,
  detailRowDefs,
  expandedWidgetVisitsColumnDefs,
  smallWidgetVisitsColumnDefs,
} from './tableColumnDefs';

import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import { DownloadMenu } from './DownloadMenu';
import { ProviderVisitsModal } from './ProviderVisitModal/ProviderVisitsModal';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

const GridWrapper = styled.div`
  background-color: ${(props) => props.theme.palette.background.paper};
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

const DownloadRow = styled.div`
  flex-shrink: 0;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

interface VisitSummaryProps {
  viewModePrint?: boolean;
  smallWidget?: boolean;
}

const excelHeaderStyle: ExcelStyle = {
  id: 'header',
  alignment: {
    horizontal: 'Center',
  },
};

export const VisitSummaryTable = ({
  viewModePrint,
  smallWidget,
}: VisitSummaryProps) => {
  const { onWidgetReady } = useContext(AppContext);
  const { dispatch, gridAPI } = useContext(VisitSummaryContext);
  const {
    crossPatientVisitSummary,
    officeFilter,
    summaryAttributeIds,
    dispatchVG,
  } = useContext(VisitsGlobalContext);
  const [visitLogOpen, setVisitLogOpen] = useState(false);

  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const columnDefs =
    (smallWidget && smallWidgetVisitsColumnDefs) ||
    expandedWidgetVisitsColumnDefs;

  const onCellClicked = (params: any) => {
    if (params.data.firstname !== 'Total') {
      const {
        personnumber,
        person_id,
        firstname,
        lastname,
        siteoffice,
      } = params.data;

      dispatchVG({
        type: SET_PROVIDER_INFO,
        payload: {
          personnumber,
          person_id,
          firstname,
          lastname,
          siteoffice,
        },
      });
      dispatchVG({
        type: TOGGLE_FILTERING_ATTRIBUTE,
        payload: summaryAttributeIds || [],
      });
      setVisitLogOpen(true);
    }
  };

  const formatHoursExcel = (val: string) => formatMMtoDec(formatHHMMtoMM(val));

  const formatPrintData = (params: any) => {
    const colName = params.column.getColDef().headerName;
    const isHHMM =
      colName === defaults.length ||
      colName === defaults.travelTime ||
      colName === defaults.visitDuration;
    const formatted = isHHMM ? formatHoursExcel(params.value) : params.value;
    return formatted;
  };

  const gridOptions: GridOptions = {
    masterDetail: !smallWidget,
    embedFullWidthRows: true,
    tooltipShowDelay: 500,
    defaultColDef: {
      ...commonColumnDefs,
    },
    columnDefs,
    ...detailRowDefs,
    onGridReady: (params: any) => {
      const gridApi: GridApi = params.api;
      if (gridApi && !gridAPI) {
        dispatch({
          type: SET_SUMMARY_GRID_API,
          payload: gridApi,
        });
      }
      onWidgetReady && onWidgetReady('VisitSummary' as mumms.ComponentName);
    },
    defaultExcelExportParams: {
      autoConvertFormulas: true,
      processCellCallback: formatPrintData,
    },
    defaultCsvExportParams: {
      processCellCallback: formatPrintData,
    },
  };

  const getGridData = () => {
    if (crossPatientVisitSummary && officeFilter !== ALL) {
      return crossPatientVisitSummary.filter(
        (e: any) => e.siteoffice === officeFilter,
      );
    }
    return crossPatientVisitSummary || undefined;
  };

  return (
    <Container id="visit-summary-table-container">
      {visitLogOpen && (
        <ProviderVisitsModal onClose={() => setVisitLogOpen(false)} />
      )}
      <div style={{ height: '100%' }}>
        <FullColumnDiv>
          {!smallWidget && (
            <DownloadRow>
              <DownloadMenu gridApi={gridAPI} />
            </DownloadRow>
          )}
          <GridWrapper
            id="visit-summary-table-wrapper"
            className={`${gridTheme.className} light max-width`}
          >
            <AgGridReact
              gridOptions={gridOptions}
              rowData={getGridData()}
              rowHeight={gridTheme.rowHeight}
              headerHeight={gridTheme.headerHeight}
              rowSelection="single"
              suppressScrollOnNewData
              detailRowAutoHeight
              onCellClicked={onCellClicked}
              excelStyles={[excelHeaderStyle]}
            />
          </GridWrapper>
        </FullColumnDiv>
      </div>
    </Container>
  );
};

export default VisitSummaryTable;
